// fetchUser.jsx - Enhanced with separate update functions for different contexts
import React from "react";
import { authenticatedFetchWithRefresh, enhancedTokenManager } from '../utils/enhancedTokenManager';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204';

// Helper function for non-authenticated requests
const publicFetch = async (url, options = {}) => {
  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });
    return response;
  } catch (error) {
    if (error.message === 'Failed to fetch' || error.name === 'NetworkError' || error.name === 'TypeError') {
      throw new Error('NETWORK_ERROR');
    }
    throw error;
  }
};

// Helper function to convert boolean values to Hebrew strings for backend
const convertToHebrewBoolean = (value) => {
  if (value === true || value === 'true' || value === 'כן') return 'כן';
  if (value === false || value === 'false' || value === 'לא') return 'לא';
  return 'לא'; // Default to 'לא'
};

// Helper function to convert role array to Hebrew boolean strings
const convertRolesToHebrewBooleans = (roles) => {
  const rolesArray = Array.isArray(roles) ? roles : [];
  return {
    IsManager: rolesArray.includes('manager') ? 'כן' : 'לא',
    IsVaad: rolesArray.includes('vaad') ? 'כן' : 'לא',
    IsTenant: rolesArray.includes('tenant') ? 'כן' : 'לא'
  };
};


export const getListUsersExternalDashboard = async (buildingId) => {
  try {

    const response = await fetch(`${API_URL}/api/users/GetUsersExternalDashboard/${buildingId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });


    // Check if response is ok
    if (!response.ok) {
      if (response.status === 404) {
        return []; // Return empty array instead of throwing error
      }

      // Try to get error message from response
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorData = await response.text();
        errorMessage = errorData || errorMessage;
      } catch (textError) {
      }

      throw new Error(`Failed to fetch users: ${errorMessage}`);
    }

    // Check if response has content
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return [];
    }

    // Try to parse JSON
    const data = await response.json();

    // Ensure we return an array
    return Array.isArray(data) ? data : [];

  } catch (error) {

    // Don't throw error for network issues, return empty array
    if (error.message === 'Failed to fetch') {
      return [];
    }

    // For parsing errors, also return empty array
    if (error.message.includes('Unexpected end of JSON input')) {
      return [];
    }

    // Re-throw other errors
    throw new Error(`Failed to fetch users: ${error.message}`);
  }
};


export const getListUsers = async (buildingId) => {
  try {
    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/GetUsers/${buildingId}`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'GetUsers failed');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const getUsersWithCustomColumns = async (buildingId) => {
  try {
    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/GetUsersWithCustomColumns/${buildingId}`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'GetUsersWithCustomColumns failed');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const getUsersByUserId = async (userId) => {
  try {
    const response = await authenticatedFetchWithRefresh(
      `${API_URL}/api/users/GetUsersByUserId?userId=${userId}`
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'GetUsers failed');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const getUserByPhone = async (phone) => {
  try {
    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/GetUserByPhone`, {
      method: 'POST',
      body: JSON.stringify(phone)
    });

    if (!response.ok) {
      const errorText = await response.text();

      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.message || errorText);
      } catch (parseError) {
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a new user - Works for both UserForm and other contexts
 * @param {Object} userData - User data
 * @returns {Promise<Object>} - API response
 */
export const createUser = async (userData) => {
  try {

    // Validation
    if (!userData.FullName?.trim()) {
      throw new Error('שם מלא הוא שדה חובה');
    }

    if (!userData.Phone?.trim()) {
      throw new Error('מספר טלפון הוא שדה חובה');
    }

    if (!userData.PasswordHash?.trim() && !userData.Password?.trim()) {
      throw new Error('סיסמה היא שדה חובה');
    }

    if (!userData.Email?.trim() && !userData.Phone?.trim()) {
      throw new Error('נדרש לפחות אמצעי קשר אחד - דוא"ל או טלפון');
    }

    // Clean and format the data properly
    const cleanedData = {
      FullName: userData.FullName?.trim(),
      Email: userData.Email?.trim() || null,
      Phone: userData.Phone?.trim(),
      PasswordHash: userData.PasswordHash?.trim() || userData.Password?.trim(),

      // Building and apartment info
      BuildingId: userData.BuildingId || userData.BuildingID,
      ApartmentID: userData.ApartmentID || userData.ApartmentId,
      ApartmentNumber: userData.ApartmentNumber,
      FloorNumber: userData.FloorNumber,

      // Role permissions - ALWAYS convert to Hebrew strings
      IsManager: convertToHebrewBoolean(userData.IsManager),
      IsVaad: convertToHebrewBoolean(userData.IsVaad),
      IsTenant: convertToHebrewBoolean(userData.IsTenant),

      // Additional fields
      RelationshipType: userData.RelationshipType,
      OwnerName: userData.OwnerName?.trim() || null,
      OwnerPhone: userData.OwnerPhone?.trim() || null,
      TenantName: userData.TenantName?.trim() || null,
      TenantPhone: userData.TenantPhone?.trim() || null,
      VaadMonthly: userData.VaadMonthly || 0,

      // Parking and Charging Station
      ParkingNumber: userData.ParkingNumber || '',
      ChargingStation: userData.ChargingStation || false,

      // Add _users field if backend requires it (empty array as default)
      _users: userData._users || []
    };

    // Remove undefined/empty fields (but keep null values for Email, OwnerName, OwnerPhone, TenantName, TenantPhone, and keep ParkingNumber)
    Object.keys(cleanedData).forEach(key => {
      if (cleanedData[key] === undefined || cleanedData[key] === '') {
        if (key !== 'Email' && key !== 'OwnerName' && key !== 'OwnerPhone' && key !== 'TenantName' && key !== 'TenantPhone' && key !== 'ParkingNumber') {
          delete cleanedData[key];
        }
      }
    });


    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/AddUser`, {
      method: 'POST',
      body: JSON.stringify(cleanedData),
    });


    if (!response.ok) {
      const errorText = await response.text();

      let errorMessage = 'שגיאה בשמירת המשתמש';

      try {
        const errorJson = JSON.parse(errorText);

        if (errorJson.message) {
          errorMessage = errorJson.message;
        } else if (errorJson.error) {
          errorMessage = errorJson.error;
        } else if (errorJson.errors) {
          const validationErrors = Object.values(errorJson.errors).flat();
          errorMessage = validationErrors.join(', ');
        }
      } catch (parseError) {
        errorMessage = errorText || `שגיאת שרת: ${response.status}`;
      }

      throw new Error(errorMessage);
    }

    const contentType = response.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();

      if (response.status >= 200 && response.status < 300) {
        return { success: true, message: 'המשתמש נוצר בהצלחה' };
      }

      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    const result = await response.json();
    return result;

  } catch (error) {
    throw error;
  }
};

/**
 * Update user for UserForm context (building management)
 * @param {Object} userData - User data to update
 * @returns {Promise<Object>} - API response
 */
export const updateUserForManagement = async (userData) => {
  try {

    if (!userData.UserID) {
      throw new Error('מזהה משתמש חסר');
    }

    if (!userData.Email?.trim() && !userData.Phone?.trim()) {
      throw new Error('נדרש לפחות אמצעי קשר אחד - דוא"ל או טלפון');
    }

    const cleanedData = {
      UserID: userData.UserID,
      FullName: userData.FullName?.trim(),
      Email: userData.Email?.trim() || null,
      Phone: userData.Phone?.trim(),
      PhoneNumber: userData.Phone?.trim(),

      // Only include password if it's being changed
      ...(userData.PasswordHash?.trim() && {
        PasswordHash: userData.PasswordHash.trim(),
        Password: userData.PasswordHash.trim()
      }),

      // Building and apartment info
      BuildingId: userData.BuildingId || userData.BuildingID,
      BuildingID: userData.BuildingId || userData.BuildingID,
      ApartmentID: userData.ApartmentID || userData.ApartmentId,
      ApartmentNumber: userData.ApartmentNumber,
      FloorNumber: userData.FloorNumber,

      // Role permissions - ALWAYS convert to Hebrew strings
      IsManager: convertToHebrewBoolean(userData.IsManager),
      IsVaad: convertToHebrewBoolean(userData.IsVaad),
      IsTenant: convertToHebrewBoolean(userData.IsTenant),

      // Additional fields
      RelationshipType: userData.RelationshipType,
      OwnerName: userData.OwnerName?.trim() || null,
      OwnerPhone: userData.OwnerPhone?.trim() || null,
      TenantName: userData.TenantName?.trim() || null,
      TenantPhone: userData.TenantPhone?.trim() || null,
      VaadMonthly: userData.VaadMonthly || 0,

      // Parking and Charging Station
      ParkingNumber: userData.ParkingNumber || '',
      ChargingStation: userData.ChargingStation || false,

      // Add _users field if backend requires it
      _users: userData._users || []
    };

    // Remove undefined/empty fields (but keep null values for Email, OwnerName, OwnerPhone, TenantName, TenantPhone, and keep ParkingNumber)
    Object.keys(cleanedData).forEach(key => {
      if (cleanedData[key] === undefined || cleanedData[key] === '') {
        if (key !== 'Email' && key !== 'OwnerName' && key !== 'OwnerPhone' && key !== 'TenantName' && key !== 'TenantPhone' && key !== 'ParkingNumber') {
          delete cleanedData[key];
        }
      }
    });


    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/UpdateUser`, {
      method: 'POST',
      body: JSON.stringify(cleanedData),
    });


    if (!response.ok) {
      const errorText = await response.text();

      let errorMessage = 'שגיאה בעדכון המשתמש';

      try {
        const errorJson = JSON.parse(errorText);

        if (errorJson.message) {
          errorMessage = errorJson.message;
        } else if (errorJson.error) {
          errorMessage = errorJson.error;
        } else if (errorJson.errors) {
          const validationErrors = Object.values(errorJson.errors).flat();
          errorMessage = validationErrors.join(', ');
        }
      } catch (parseError) {
        errorMessage = errorText || `שגיאת שרת: ${response.status}`;
      }

      throw new Error(errorMessage);
    }

    const contentType = response.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();

      if (response.status >= 200 && response.status < 300) {
        return { success: true, message: 'המשתמש עודכן בהצלחה' };
      }

      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
};

/**
 * Update user for Profile context (user updating their own profile)
 * @param {Object} userData - User data to update
 * @returns {Promise<Object>} - API response
 */
export const updateUserProfile = async (userData) => {
  try {

    if (!userData.UserID) {
      throw new Error('מזהה משתמש חסר');
    }

    if (!userData.Email?.trim() && !userData.Phone?.trim()) {
      throw new Error('נדרש לפחות אמצעי קשר אחד - דוא"ל או טלפון');
    }

    // Convert role array to Hebrew boolean strings if roles are provided
    let roleData = {};
    if (userData.role && Array.isArray(userData.role)) {
      roleData = convertRolesToHebrewBooleans(userData.role);
    } else {
      // Use existing boolean values if provided
      roleData = {
        IsManager: convertToHebrewBoolean(userData.IsManager),
        IsVaad: convertToHebrewBoolean(userData.IsVaad),
        IsTenant: convertToHebrewBoolean(userData.IsTenant)
      };
    }

    const cleanedData = {
      UserID: userData.UserID,
      FullName: userData.FullName?.trim(),
      Email: userData.Email?.trim() || null,
      Phone: userData.Phone?.trim(),
      PhoneNumber: userData.Phone?.trim(),
      ApartmentNumber: userData.ApartmentNumber,
      BuildingID: userData.BuildingID,

      // Role permissions as Hebrew strings
      ...roleData,

      // Profile picture fields
      ProfilePicture: userData.ProfilePicture,
      ProfileImagePath: userData.ProfileImagePath,

      // Add _users field if backend requires it
      _users: userData._users || []
    };

    // Remove undefined/empty fields (but keep null values for Email, OwnerName, OwnerPhone)
    Object.keys(cleanedData).forEach(key => {
      if (cleanedData[key] === undefined || cleanedData[key] === '') {
        if (key !== 'Email' && key !== 'OwnerName' && key !== 'OwnerPhone') {
          delete cleanedData[key];
        }
      }
    });


    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/UpdateUser`, {
      method: 'POST',
      body: JSON.stringify(cleanedData),
    });

    if (!response.ok) {
      const errorText = await response.text();

      let errorMessage = 'שגיאה בעדכון הפרופיל';

      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.message) {
          errorMessage = errorJson.message;
        } else if (errorJson.errors) {
          const validationErrors = Object.values(errorJson.errors).flat();
          errorMessage = validationErrors.join(', ');
        }
      } catch (parseError) {
        errorMessage = errorText || `שגיאת שרת: ${response.status}`;
      }

      throw new Error(errorMessage);
    }

    const contentType = response.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      if (response.status >= 200 && response.status < 300) {
        return { success: true, message: 'הפרופיל עודכן בהצלחה' };
      }
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
};

/**
 * Legacy updateUser function - now routes to appropriate specific function
 * @param {Object} userData - User data to update
 * @returns {Promise<Object>} - API response
 */
export const updateUser = async (userData) => {
  // Determine context based on the data structure
  if (userData.role && Array.isArray(userData.role)) {
    // Profile context - has role array
    return await updateUserProfile(userData);
  } else {
    // Management context - has individual boolean fields
    return await updateUserForManagement(userData);
  }
};

/**
 * Update UserBuilding role information
 * @param {Object} userBuildingData - UserBuilding data to update
 * @returns {Promise<Object>} - API response
 */
export const updateUserBuilding = async (userBuildingData) => {
  try {

    if (!userBuildingData.UserBuildingID) {
      throw new Error('מזהה UserBuilding חסר');
    }

    const cleanedData = {
      UserBuildingID: userBuildingData.UserBuildingID,
      IsManager: userBuildingData.IsManager || 'false',
      IsVaad: userBuildingData.IsVaad || 'false',
      IsTenant: userBuildingData.IsTenant || 'false'
    };


    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/UpdateUserBuilding`, {
      method: 'POST',
      body: JSON.stringify(cleanedData),
    });

    if (!response.ok) {
      const errorText = await response.text();

      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(`שגיאת שרת: ${errorJson.message || errorText}`);
      } catch (parseError) {
        throw new Error(`שגיאת שרת: ${response.status} - ${errorText}`);
      }
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
};

// Wrapper functions for backwards compatibility
export const createUserWithBuilding = async (userData) => {
  return await createUser(userData);
};

export const updateUserBuildingRole = async (userData) => {
  return await updateUserForManagement(userData);
};

// Rest of the existing functions remain the same...
export const deleteUser = async (userId, buildingId = null) => {
  try {
    // Use the smart deletion logic
    const result = await deleteUserSmart(userId, buildingId);
    
    if (!result.success) {
      // Convert to legacy format for backwards compatibility
      const error = new Error(result.reason || 'לא ניתן למחוק את המשתמש');
      error.canSoftDelete = result.suggestedActions?.includes('softDelete');
      error.isManager = result.isManager;
      error.dependencies = result.dependencies;
      error.suggestedActions = result.suggestedActions;
      throw error;
    }
    
    return result;
  } catch (error) {
    console.error('deleteUser error:', error);
    throw error;
  }
};

/**
 * Remove user from specific apartment or all apartments in building (FALLBACK VERSION)
 * @param {number} userId - User ID
 * @param {number} buildingId - Building ID  
 * @param {number} apartmentId - Apartment ID (optional)
 * @returns {Promise<Object>} - API response
 */
export const removeUserFromApartment = async (userId, buildingId, apartmentId = null) => {
  try {
    // Try the new endpoint first
    try {
      const requestData = {
        UserID: userId,
        BuildingID: buildingId,
        ...(apartmentId && { ApartmentID: apartmentId })
      };

      const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/RemoveUserFromApartment`, {
        method: 'DELETE',
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        return result;
      }
    } catch (newEndpointError) {
    }

    // Fallback: Get user details first to check roles
    let userDetails;
    try {
      userDetails = await getUserByIdDetails(userId, buildingId);
    } catch (error) {
      console.warn('Could not get user details, proceeding with basic removal');
    }

    const isManager = userDetails?.IsManager === 'כן';
    const isVaad = userDetails?.IsVaad === 'כן';

    if (apartmentId) {
      // Removing from specific apartment - need to update tenant status intelligently
      if (userDetails && (isManager || isVaad)) {
        // Manager or Vaad member - update to remove tenant status but keep other roles
        const updateData = {
          UserID: userId,
          FullName: userDetails.FullName,
          Email: userDetails.Email,
          Phone: userDetails.Phone,
          BuildingId: buildingId,
          ApartmentID: null, // Remove apartment association
          ApartmentNumber: null,
          FloorNumber: userDetails.FloorNumber,
          RelationshipType: userDetails.RelationshipType,
          VaadMonthly: userDetails.VaadMonthly,
          IsManager: userDetails.IsManager,
          IsVaad: userDetails.IsVaad,
          IsTenant: 'לא' // No longer a tenant
        };

        const result = await updateUserForManagement(updateData);
        
        return {
          message: `המשתמש הוסר מהדירה אך נשאר בבניין כ${isManager && isVaad ? 'מנהל וחבר ועד' : isManager ? 'מנהל' : 'חבר ועד'}`,
          removedFromBuilding: false,
          stillInBuilding: true,
          isTenant: 'לא',
          userID: userId,
          apartmentID: apartmentId
        };
      }
    }

    // Fallback to existing endpoint for complete removal
    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/DeAssociateUserAppartment`, {
      method: 'DELETE',
      body: JSON.stringify({ UserID: userId })
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = 'שגיאה בהסרת המשתמש מהדירה';

      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorMessage;
      } catch (parseError) {
        errorMessage = errorText || `שגיאת שרת: ${response.status}`;
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();
    // Transform response to match expected format
    return {
      message: 'המשתמש הוסר מהדירה בהצלחה',
      removedFromBuilding: true, // Existing endpoint removes completely
      stillInBuilding: false,
      isTenant: 'לא',
      userID: userId,
      apartmentID: apartmentId
    };
  } catch (error) {
    console.error('removeUserFromApartment error:', error);
    throw error;
  }
};


/**
 * Remove manager role from user (FALLBACK VERSION)
 * @param {number} userId - User ID
 * @param {number} buildingId - Building ID
 * @returns {Promise<Object>} - API response
 */
export const removeManagerRole = async (userId, buildingId) => {
  try {
    // Try the new endpoint first
    try {
      const requestData = {
        UserID: userId,
        BuildingID: buildingId
      };

      const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/RemoveManagerRole`, {
        method: 'POST',
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        return result;
      }
    } catch (newEndpointError) {
    }

    // Fallback: Update user to remove manager role
    // First get the user details
    const user = await getUserByIdDetails(userId, buildingId);
    
    // Determine tenant status based on whether user has apartments
    const buildingUsers = await getListUsers(buildingId);
    const userInBuilding = buildingUsers.find(u => u.userID === userId);
    const hasApartment = userInBuilding && userInBuilding.apartmentNumber && userInBuilding.apartmentNumber !== 'לא צוין';
    
    // Update user with manager role removed and proper tenant status
    const updateData = {
      UserID: userId,
      FullName: user.FullName,
      Email: user.Email,
      Phone: user.Phone,
      BuildingId: buildingId,
      ApartmentID: user.ApartmentID,
      ApartmentNumber: user.ApartmentNumber,
      FloorNumber: user.FloorNumber,
      RelationshipType: user.RelationshipType,
      VaadMonthly: user.VaadMonthly,
      IsManager: 'לא', // Remove manager role
      IsVaad: user.IsVaad || 'לא',
      IsTenant: hasApartment ? 'כן' : (user.IsVaad === 'כן' ? user.IsTenant : 'לא') // Keep tenant if has apartment, or if vaad member
    };

    const result = await updateUserForManagement(updateData);
    
    const statusMessage = hasApartment 
      ? "הרשאות הניהול הוסרו בהצלחה, המשתמש נשאר כדייר"
      : user.IsVaad === 'כן' 
        ? "הרשאות הניהול הוסרו בהצלחה, המשתמש נשאר כחבר ועד"
        : "הרשאות הניהול הוסרו בהצלחה";
    
    return {
      message: statusMessage,
      userID: userId,
      buildingID: buildingId,
      hasApartments: hasApartment,
      newRoles: {
        isManager: 'לא',
        isVaad: user.IsVaad || 'לא',
        isTenant: updateData.IsTenant
      }
    };
  } catch (error) {
    console.error('removeManagerRole error:', error);
    throw error;
  }
};

/**
 * Helper function to get user details for fallback operations
 */
const getUserByIdDetails = async (userId, buildingId) => {
  try {
    // Try to get user from the building users list
    const buildingUsers = await getListUsers(buildingId);
    const user = buildingUsers.find(u => u.userID === userId);
    
    if (user) {
      return {
        UserID: user.userID,
        FullName: user.fullName,
        Email: user.email,
        Phone: user.phone,
        IsVaad: user.isVaad,
        IsTenant: user.isTenant,
        IsManager: user.isManager,
        RelationshipType: user.relationshipType,
        ApartmentNumber: user.apartmentNumber,
        FloorNumber: user.floorNumber,
        VaadMonthly: user.vaadMonthly,
        ApartmentID: user.apartmentID,
        BuildingId: user.buildingID
      };
    }
    
    throw new Error('User not found in building');
  } catch (error) {
    console.error('Error getting user details:', error);
    throw error;
  }
};


/**
 * Complete user deletion (only for users without dependencies)
 * @param {number} userId - User ID
 * @param {number} buildingId - Building ID (optional)
 * @returns {Promise<Object>} - API response
 */
export const deleteUserCompletely = async (userId, buildingId = null) => {
  try {
    const requestData = {
      UserID: userId,
      ...(buildingId && { BuildingId: buildingId })
    };

    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/DeleteUserCompletely`, {
      method: 'DELETE',
      body: JSON.stringify(requestData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = 'שגיאה במחיקת המשתמש';

      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.isManager) {
          // Return special response for manager users
          return {
            success: false,
            isManager: true,
            canSoftDelete: true,
            message: errorJson.message,
            suggestedActions: ['removeFromBuilding', 'removeManagerRole']
          };
        }
        errorMessage = errorJson.message || errorMessage;
      } catch (parseError) {
        errorMessage = errorText || `שגיאת שרת: ${response.status}`;
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();
    return { success: true, ...result };
  } catch (error) {
    console.error('deleteUserCompletely error:', error);
    throw error;
  }
};

/**
 * Enhanced delete user with smart handling of managers and dependencies
 * @param {number} userId - User ID
 * @param {number} buildingId - Building ID (optional)
 * @returns {Promise<Object>} - API response with action suggestions
 */
export const deleteUserSmart = async (userId, buildingId = null) => {
  try {
    // First check user dependencies
    const dependencyCheck = await checkUserDependencies(userId);
    
    if (!dependencyCheck.canDelete) {
      return {
        success: false,
        canDelete: false,
        reason: dependencyCheck.reason,
        dependencies: dependencyCheck.dependencies,
        suggestedActions: ['softDelete', 'removeFromBuilding'],
        requiresConfirmation: true
      };
    }

    // If user can be deleted, try the enhanced delete
    return await deleteUserCompletely(userId, buildingId);

  } catch (error) {
    console.error('deleteUserSmart error:', error);
    throw error;
  }
};


/**
 * Show user deletion options based on their role and dependencies
 * @param {Object} user - User object with role information
 * @param {number} buildingId - Building ID
 * @param {number} apartmentId - Apartment ID (optional)
 * @returns {Promise<Object>} - Available actions and their descriptions
 */
export const getUserDeletionOptions = async (user, buildingId, apartmentId = null) => {
  try {
    const isManager = user.isManager === "כן" || user.IsManager === "כן";
    const options = [];

    // Always available: Remove from apartment (if apartment specified)
    if (apartmentId) {
      options.push({
        action: 'removeFromApartment',
        label: `הסר מדירה ${user.apartmentNumber || apartmentId}`,
        description: 'הסרה מדירה ספציפית, המשתמש יישאר בבניין',
        severity: 'low'
      });
    }

    // Manager-specific options
    if (isManager) {
      options.push({
        action: 'removeManagerRole',
        label: 'הסר הרשאות ניהול',
        description: 'הסרת הרשאות ניהול בלבד, המשתמש יישאר כדייר',
        severity: 'medium'
      });

      options.push({
        action: 'removeFromBuilding',
        label: 'הסר מהבניין',
        description: 'הסרה מהבניין לגמרי אך שמירת רשומת המשתמש',
        severity: 'high'
      });
    } else {
      // Regular user options
      options.push({
        action: 'removeFromBuilding',
        label: 'הסר מהבניין',
        description: 'הסרה מהבניין לגמרי',
        severity: 'medium'
      });
    }

    // Check if complete deletion is possible
    try {
      const dependencyCheck = await checkUserDependencies(user.userID || user.UserID);
      
      if (dependencyCheck.canDelete && !isManager) {
        options.push({
          action: 'deleteCompletely',
          label: 'מחק לחלוטין',
          description: 'מחיקה מוחלטת מהמערכת (בלתי הפיכה)',
          severity: 'critical'
        });
      } else {
        options.push({
          action: 'softDelete',
          label: 'הסרה עם שמירת היסטוריה',
          description: `לא ניתן למחוק בגלל: ${dependencyCheck.dependencies?.join(', ') || 'תלויות במערכת'}`,
          severity: 'medium',
          disabled: false
        });
      }
    } catch (error) {
      console.warn('Could not check dependencies:', error);
    }

    return {
      success: true,
      options,
      userInfo: {
        isManager,
        userName: user.fullName || user.FullName,
        apartmentNumber: user.apartmentNumber,
        buildingId
      }
    };

  } catch (error) {
    console.error('getUserDeletionOptions error:', error);
    throw error;
  }
};

/**
 * Execute user deletion based on selected action
 * @param {string} action - Action type (removeFromApartment, removeManagerRole, etc.)
 * @param {number} userId - User ID
 * @param {number} buildingId - Building ID
 * @param {number} apartmentId - Apartment ID (optional)
 * @returns {Promise<Object>} - API response
 */
export const executeUserDeletionAction = async (action, userId, buildingId, apartmentId = null) => {
  try {
    switch (action) {
      case 'removeFromApartment':
        return await removeUserFromApartment(userId, buildingId, apartmentId);

      case 'removeManagerRole':
        return await removeManagerRole(userId, buildingId);

      case 'removeFromBuilding':
        // Remove user from building completely (removes from UserBuilding table)
        try {
          const requestBody = {
            UserID: userId,
            BuildingID: buildingId
          };

          // Call the new RemoveUserFromBuilding endpoint that actually removes the UserBuilding record
          const response = await authenticatedFetchWithRefresh(`${API_URL}/api/Users/RemoveUserFromBuilding`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
          });
          
          if (response.ok) {
            const result = await response.json();

            // Refresh the user list
            window.dispatchEvent(new Event('userDataChanged'));
            
            return { 
              success: true, 
              message: result.message || 'המשתמש הוסר מהבניין בהצלחה',
              ...result 
            };
          } else {
            // If the endpoint fails, get the error message
            let errorMessage = 'שגיאה בהסרת המשתמש מהבניין';
            try {
              const errorData = await response.json();
              errorMessage = errorData.message || errorMessage;
            } catch (e) {
              console.error('Could not parse error response');
            }
            
            throw new Error(errorMessage);
          }
        } catch (error) {
          console.error('❌ Error removing user from building:', error);
          throw new Error(`שגיאה בהסרת המשתמש מהבניין: ${error.message}`);
        }

      case 'deleteCompletely':
        // Use existing delete function
        return await deleteUser(userId);

      case 'softDelete':
        // Use existing soft delete function
        return await softDeleteUser(userId);

      default:
        throw new Error(`פעולה לא מוכרת: ${action}`);
    }
  } catch (error) {
    console.error('executeUserDeletionAction error:', error);
    throw error;
  }
};


export const deAssociateUserAppartment = async (userId) => {
  console.warn('deAssociateUserAppartment is deprecated, use removeUserFromApartment instead');
  try {
    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/DeAssociateUserAppartment`, {
      method: 'DELETE',
      body: JSON.stringify({ UserID: userId })
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to delete user');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};


/**
 * Batch user deletion operations
 * @param {Array} userActions - Array of {userId, action, buildingId, apartmentId}
 * @returns {Promise<Object>} - Batch operation results
 */
export const batchUserDeletion = async (userActions) => {
  try {
    const results = {
      successful: [],
      failed: [],
      total: userActions.length
    };

    for (const userAction of userActions) {
      try {
        const result = await executeUserDeletionAction(
          userAction.action,
          userAction.userId,
          userAction.buildingId,
          userAction.apartmentId
        );

        results.successful.push({
          userId: userAction.userId,
          action: userAction.action,
          result
        });
      } catch (error) {
        results.failed.push({
          userId: userAction.userId,
          action: userAction.action,
          error: error.message
        });
      }
    }

    return results;
  } catch (error) {
    console.error('batchUserDeletion error:', error);
    throw error;
  }
};



// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `HTTP error! status: ${response.status}`);
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return await response.json();
  }

  return null;
};

export const registerUser = async (userData) => {
  try {
    const userPayload = {
      FullName: userData.fullName,
      Email: userData.email,
      Password: userData.password,
      Phone: userData.phone,
      IsManager: "כן",
      IsVaad: userData.isVaad,
      IsTenant: "לא",
      City: userData.city,
      Address: userData.address,
      AddressNumber: userData.addressNumber,
      ApartmentNumber: userData.apartmentNumber || null,
      NumApartments: parseInt(userData.numApartments) || null,
      NumFloors: parseInt(userData.numFloors) || null,
      CreateDate: userData.createDate ? new Date(userData.createDate).toISOString() : new Date().toISOString(),
      CreateDateString: userData.createDate
    };

    const response = await fetch(`${API_URL}/api/Auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userPayload)
    });

    return await handleResponse(response);
  } catch (error) {
    throw error;
  }
};

export const verifyEmail = async (email, code) => {
  try {
    const response = await publicFetch(`${API_URL}/api/auth/verify-email`, {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Verification failed');
    }

    return data;
  } catch (error) {
    console.error('❌ verifyEmail error:', error);
    throw error;
  }
};

export const resendVerificationCode = async (email) => {
  try {
    const response = await publicFetch(`${API_URL}/api/auth/resend-verification`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to resend code');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

export const completeRegistration = async (email) => {
  try {
    const response = await publicFetch(`${API_URL}/api/auth/complete-registration`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to complete registration');
    }

    return data;
  } catch (error) {
    console.error('❌ completeRegistration error:', error);
    throw error;
  }
};

export const loginUser = async (credentials) => {
  try {
    const response = await publicFetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      body: JSON.stringify(credentials)
    });

    const data = await response.json();
    
    if (!response.ok) {
      if (data.requiresVerification) {
        const error = new Error(data.message);
        error.requiresVerification = true;
        error.email = data.email;
        throw error;
      }
      throw new Error(data.message || 'Login failed');
    }

    return data;
  } catch (error) {
    console.error('❌ loginUser error:', error);
    throw error;
  }
};

export const getUserBuildingRole = async (userId, buildingId) => {
  try {
    const response = await authenticatedFetchWithRefresh(
      `${API_URL}/api/users/GetUserBuildingRole?userId=${userId}&buildingId=${buildingId}`
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to get user building role');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const getUserBuildings = async (userId) => {
  try {
    const response = await authenticatedFetchWithRefresh(
      `${API_URL}/api/users/GetUserBuildings/${userId}`
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to get user buildings');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const removeUserFromBuilding = async (userBuildingId) => {
  try {
    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/RemoveUserFromBuilding`, {
      method: 'DELETE',
      body: JSON.stringify({ UserBuildingID: userBuildingId })
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to remove user from building');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const getCurrentProfile = async (userData) => {
  try {
    const email = typeof userData === 'string' ? userData : userData.email || userData.Email;

    if (!email) {
      throw new Error('Email is required to fetch user profile');
    }


    const token = enhancedTokenManager.getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_URL}/api/users/GetUserByEmail`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(email)
    });

    if (!response.ok) {
      const errorText = await response.text();

      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.message || errorText);
      } catch (parseError) {
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

export const getCurrentProfileEnhanced = async (userData) => {
  try {
    let searchData = {};

    if (typeof userData === 'string') {
      if (userData.includes('@')) {
        searchData.email = userData;
      } else {
        searchData.phone = userData;
      }
    } else if (typeof userData === 'object') {
      searchData.email = userData.email || userData.Email;
      searchData.phone = userData.phone || userData.Phone;
    }


    if (searchData.email) {
      try {
        return await getCurrentProfile(searchData.email);
      } catch (error) {
      }
    }

    if (searchData.phone) {
      try {
        return await getUserByPhone(searchData.phone);
      } catch (error) {
      }
    }

    throw new Error('לא ניתן למצוא את המשתמש - חסר דוא"ל או טלפון');
  } catch (error) {
    throw error;
  }
};

export const changePassword = async (passwordData) => {
  try {
    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/user/change-password`, {
      method: 'POST',
      body: JSON.stringify(passwordData)
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to change password');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const forgotPassword = async (email) => {
  try {

    const response = await publicFetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      body: JSON.stringify({ email: email.trim() })
    });


    const data = await response.json();

    if (!response.ok) {
      if (response.status === 400 && data.message) {
        throw new Error(data.message);
      } else if (response.status === 500) {
        throw new Error('שגיאה בשרת. אנא נסה שוב מאוחר יותר.');
      } else {
        throw new Error(data.message || 'שגיאה בשליחת קוד האיפוס');
      }
    }

    return data;
  } catch (error) {

    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      throw new Error('שגיאה בחיבור לשרת. אנא בדוק את החיבור לאינטרנט.');
    }

    throw error;
  }
};

export const verifyResetCode = async (email, code) => {
  try {

    const response = await publicFetch(`${API_URL}/api/auth/verify-reset-code`, {
      method: 'POST',
      body: JSON.stringify({
        email: email.trim(),
        code: code.trim()
      })
    });


    const data = await response.json();

    if (!response.ok) {
      if (response.status === 400 && data.message) {
        throw new Error(data.message);
      } else if (response.status === 500) {
        throw new Error('שגיאה בשרת. אנא נסה שוב מאוחר יותר.');
      } else {
        throw new Error(data.message || 'קוד אימות שגוי');
      }
    }

    return data;
  } catch (error) {

    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      throw new Error('שגיאה בחיבור לשרת. אנא בדוק את החיבור לאינטרנט.');
    }

    throw error;
  }
};

export const resetPassword = async (email, code, newPassword) => {
  try {

    const response = await publicFetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      body: JSON.stringify({
        email: email.trim(),
        code: code.trim(),
        newPassword: newPassword
      })
    });


    const data = await response.json();

    if (!response.ok) {
      if (response.status === 400 && data.message) {
        throw new Error(data.message);
      } else if (response.status === 500) {
        throw new Error('שגיאה בשרת. אנא נסה שוב מאוחר יותר.');
      } else {
        throw new Error(data.message || 'שגיאה בשינוי הסיסמה');
      }
    }

    return data;
  } catch (error) {

    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      throw new Error('שגיאה בחיבור לשרת. אנא בדוק את החיבור לאינטרנט.');
    }

    throw error;
  }
};

export const refreshUserToken = async () => {
  try {
    return await enhancedTokenManager.refreshToken();
  } catch (error) {
    throw error;
  }
};

export const isTokenValid = () => {
  const token = enhancedTokenManager.getToken();
  return token && !enhancedTokenManager.isTokenExpired(token);
};

export const getTokenInfo = () => {
  const token = enhancedTokenManager.getToken();
  if (!token) return null;

  try {
    const payload = enhancedTokenManager.decodeToken(token);
    if (!payload) return null;

    const expiryDate = new Date(payload.exp * 1000);
    const currentDate = new Date();
    const timeRemaining = expiryDate.getTime() - currentDate.getTime();

    return {
      expiryDate,
      currentDate,
      timeRemainingMinutes: Math.floor(timeRemaining / 60000),
      timeRemainingHours: Math.floor(timeRemaining / 3600000),
      isExpired: timeRemaining <= 0,
      shouldRefresh: enhancedTokenManager.shouldRefresh(token)
    };
  } catch (error) {
    return null;
  }
};

export const logoutUser = () => {
  enhancedTokenManager.removeToken();
  localStorage.removeItem('pendingVerification');
};

export const getAllUsersForAdmin = async (page = 1, pageSize = 10, search = '') => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search: search || ''
    });

    const response = await authenticatedFetchWithRefresh(
      `${API_URL}/api/users/GetAllUsersForAdmin?${queryParams}`
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to fetch users for admin');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const checkUserDependencies = async (userId) => {
  try {
    const response = await authenticatedFetchWithRefresh(
      `${API_URL}/api/users/CheckUserDependencies/${userId}`
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to check user dependencies');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const softDeleteUser = async (userId) => {
  try {
    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/SoftDeleteUser`, {
      method: 'POST',
      body: JSON.stringify({ UserID: userId })
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to soft delete user');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const deleteUserEnhanced = async (userId) => {
  try {
    const dependencyCheck = await checkUserDependencies(userId);

    if (!dependencyCheck.canDelete) {
      return {
        success: false,
        canDelete: false,
        reason: dependencyCheck.reason,
        dependencies: dependencyCheck.dependencies,
        requiresConfirmation: true
      };
    }

    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/DeleteUser`, {
      method: 'DELETE',
      body: JSON.stringify({ UserID: userId })
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to delete user');
    }

    const result = await response.json();
    return {
      success: true,
      canDelete: true,
      ...result
    };
  } catch (error) {
    throw error;
  }
};

export const validateUserContact = (userData) => {
  const errors = {};

  if (!userData.Email && !userData.Phone) {
    errors.general = 'נדרש לפחות אמצעי קשר אחד - דוא"ל או טלפון';
    return { isValid: false, errors };
  }

  if (userData.Email && userData.Email.trim()) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.Email)) {
      errors.Email = 'כתובת דוא"ל לא תקינה';
    }
  }

  if (userData.Phone && userData.Phone.trim()) {
    const phoneRegex = /^[0-9]{9,10}$/;
    const cleanPhone = userData.Phone.replace(/[-\s]/g, '');
    if (!phoneRegex.test(cleanPhone)) {
      errors.Phone = 'מספר טלפון לא תקין (9-10 ספרות)';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Create multiple users from Excel data - Bulk operation
 * @param {Array} usersData - Array of user data objects
 * @returns {Promise<Object>} - API response with success and error details
 */
export const createUsersFromExcel = async (usersData) => {
  try {
    if (!Array.isArray(usersData) || usersData.length === 0) {
      throw new Error('נתוני המשתמשים חסרים או לא תקינים');
    }

    // Clean and format each user data properly
    const cleanedUsersData = usersData.map((userData, index) => {
      // Validation for each user
      if (!userData.FullName?.trim()) {
        throw new Error(`שם מלא חסר עבור משתמש במיקום ${index + 1}`);
      }

      // If neither email nor phone is provided, we'll let the backend handle it
      // (it can generate temporary values)

      return {
        FullName: userData.FullName?.trim(),
        Email: userData.Email?.trim() || null,
        Phone: userData.Phone?.trim() || null,
        PasswordHash: userData.PasswordHash?.trim() || userData.Password?.trim() || null,

        // Building and apartment info
        BuildingId: userData.BuildingId || userData.BuildingID,
        ApartmentID: userData.ApartmentID || userData.ApartmentId,
        ApartmentNumber: userData.ApartmentNumber,
        FloorNumber: userData.FloorNumber,

        // Role permissions - convert to Hebrew strings
        IsManager: convertToHebrewBoolean(userData.IsManager || false),
        IsVaad: convertToHebrewBoolean(userData.IsVaad || false),
        IsTenant: convertToHebrewBoolean(userData.IsTenant !== false ? true : false), // Default to true for Excel imports

        // Additional fields
        RelationshipType: userData.RelationshipType || 'בעלים',
        OwnerName: userData.OwnerName?.trim() || null,
        OwnerPhone: userData.OwnerPhone?.trim() || null,
        VaadMonthly: userData.VaadMonthly || 0
      };
    });

    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/AddUsersFromExcel`, {
      method: 'POST',
      body: JSON.stringify(cleanedUsersData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Excel upload error response:', errorText);

      let errorMessage = 'שגיאה בשמירת המשתמשים';

      try {
        const errorJson = JSON.parse(errorText);

        if (errorJson.message) {
          errorMessage = errorJson.message;
        } else if (errorJson.error) {
          errorMessage = errorJson.error;
        } else if (errorJson.errors) {
          const validationErrors = Object.values(errorJson.errors).flat();
          errorMessage = validationErrors.join(', ');
        }
      } catch (parseError) {
        errorMessage = errorText || `שגיאת שרת: ${response.status}`;
      }

      throw new Error(errorMessage);
    }

    const contentType = response.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      console.warn('Non-JSON response:', responseText);

      if (response.status >= 200 && response.status < 300) {
        return { 
          success: true, 
          message: 'המשתמשים נוצרו בהצלחה',
          successCount: usersData.length,
          errorCount: 0,
          successes: [],
          errors: []
        };
      }

      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    const result = await response.json();
    return result;

  } catch (error) {
    console.error('createUsersFromExcel error:', error);
    throw error;
  }
};

/**
 * Validate Excel user data before sending to server
 * @param {Array} usersData - Array of user data objects
 * @returns {Object} - Validation result with isValid flag and errors array
 */
export const validateExcelUsersData = (usersData) => {
  const errors = [];

  if (!Array.isArray(usersData) || usersData.length === 0) {
    return {
      isValid: false,
      errors: ['לא נמצאו נתוני משתמשים לעיבוד']
    };
  }

  usersData.forEach((userData, index) => {
    const userErrors = [];
    const rowNumber = index + 1;

    // Check required field - FullName
    if (!userData.FullName || !userData.FullName.trim()) {
      userErrors.push(`שורה ${rowNumber}: שם מלא חסר`);
    }

    // Email validation - only if provided
    if (userData.Email && userData.Email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userData.Email.trim())) {
        userErrors.push(`שורה ${rowNumber}: כתובת דוא"ל לא תקינה`);
      }
    }

    // Phone validation - only if provided
    if (userData.Phone && userData.Phone.trim()) {
      const phoneRegex = /^[\d\-\s\+\(\)]{9,15}$/;
      if (!phoneRegex.test(userData.Phone.trim())) {
        userErrors.push(`שורה ${rowNumber}: מספר טלפון לא תקין`);
      }
    }

    // At least one contact method should be provided (email or phone)
    // If neither is provided, the backend will generate temporary values
    if (!userData.Email?.trim() && !userData.Phone?.trim()) {
      // This is just a warning, not an error - backend can handle this
      console.warn(`Row ${rowNumber}: No email or phone provided, temporary values will be generated`);
    }

    errors.push(...userErrors);
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Get all users for admin dashboard (with email verification and building info)
export const getAllUsersAdmin = async () => {
  try {
    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/Users/GetAllUsers`);
    
    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    throw error;
  }
};

// Add user as manager to building (manager only endpoint)
export const addUserManager = async (userData) => {
  try {
    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/Users/AddUserManager`, {
      method: 'POST',
      body: JSON.stringify(userData)
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to add user manager');
    }
    
    return data;
  } catch (error) {
    console.error('addUserManager error:', error);
    throw error;
  }
};

/**
 * Update VaadMonthly amount for all users in a building
 * @param {number} buildingId - Building ID
 * @param {number} amount - Amount to add or subtract
 * @param {string} operation - 'add' or 'subtract'
 * @returns {Promise<Object>} - API response with updated count
 */
export const updateVaadMonthlyForBuilding = async (buildingId, amount, operation) => {
  try {
    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/users/UpdateVaadMonthlyForBuilding`, {
      method: 'POST',
      body: JSON.stringify({
        BuildingId: buildingId,
        Amount: amount,
        Operation: operation // 'add' or 'subtract'
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = 'שגיאה בעדכון סכום ועד בית';

      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('updateVaadMonthlyForBuilding error:', error);
    throw error;
  }
};